import { z } from 'zod';

import {
  isNieDocumentType,
  isNieEuDocumentType,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/utils/nieDocumentTypes.utils';
import { DocumentIdFormObjectSchema } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/schemas/DocumentIdFormSchema';
import {
  NieDataEuFormSchema,
  NieDataFormSchema,
  NieDataFormType,
} from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/schemas/nieDataForm.schema';
import { GetTextIdTranslationType } from '@/components/shared/hooks/useTranslation';
import { validateDocumentNumber } from '@/utils/zodValidation/validateDocumentNumber';

export const DocumentIdWithNieDataFormSchema = (
  t: GetTextIdTranslationType,
  showBirthDateField: boolean,
  nieDataRequired: boolean,
) =>
  DocumentIdFormObjectSchema(t, showBirthDateField)
    .merge(NieDataFormSchema(t).partial())
    .superRefine((data, ctx) => {
      if (!isNieDocumentType(data.documentType) || !nieDataRequired) {
        return;
      }

      const nieFormData = data as Partial<NieDataFormType>;
      const nieSchema = isNieEuDocumentType(data.documentType) ? NieDataEuFormSchema(t) : NieDataFormSchema(t);
      const requiredNieFields = Object.keys(nieSchema.shape) as (keyof NieDataFormType)[];

      let hasMissingRequiredFields = false;

      requiredNieFields.forEach((fieldName) => {
        if (nieFormData[fieldName] == null) {
          hasMissingRequiredFields = true;
          ctx.addIssue({
            code: 'custom',
            message: t('REQUIRED_FIELD'),
            path: [fieldName],
          });
        }
      });

      if (hasMissingRequiredFields) {
        return;
      }

      const nieResult = nieSchema.safeParse(
        requiredNieFields.reduce(
          (nieValues, fieldName) => ({
            ...nieValues,
            [fieldName]: nieFormData[fieldName],
          }),
          {} as Partial<NieDataFormType>,
        ),
      );

      if (!nieResult.success) {
        nieResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            code: 'custom',
            message: issue.message,
            path: issue.path,
          });
        });
      }
    })
    .refine((data) => validateDocumentNumber(data.documentNumber, data.documentType), {
      message: t('INVALID_DOCUMENT_NUMBER'),
      path: ['documentNumber'],
    });

export type DocumentIdWithNieDataFormType = z.infer<ReturnType<typeof DocumentIdWithNieDataFormSchema>>;
